import { decorate, inject, injectable } from "inversify";
import { ActionContext } from "vuex";
import {
  Action,
  Module,
  Mutation,
  RegisterOptions,
  VuexModule
} from "vuex-class-modules";

try {
  decorate(injectable(), VuexModule);
  // eslint-disable-next-line
} catch {}

@injectable()
@Module
export default class AuthModule extends VuexModule {
  loggedIn: boolean = false;
  loggedOut: boolean = true;
  roles: Array<string> = [];

  /**
   * We do not want the token to be reactive. Otherwise every time the token is
   * refreshed, computed properties, which access the backend will re-run.
   *
   * Cf. https://stackoverflow.com/a/47452775/1534459
   */
  nonReactive = Object.freeze({
    data: {
      _token: null as string | null
    }
  });

  constructor(
    @inject("VuexModuleOptions") options: (className: string) => RegisterOptions
  ) {
    super(options("AuthModule"));
  }

  get hasRole(): (role: string) => boolean {
    return role => this.roles.includes(role);
  }

  get isLoggedIn() {
    return this.loggedIn;
  }
  get isLoggedOut() {
    return this.loggedOut;
  }

  /**
   * Do not use a getter, because it would store the token and prevent the
   * refreshed token from being used.
   */
  getToken() {
    return this.nonReactive.data._token;
  }

  @Mutation
  login(token: string) {
    this.nonReactive.data._token = token;
    this.loggedIn = true;
    this.loggedOut = false;
  }

  @Mutation
  updateToken(token: string) {
    this.nonReactive.data._token = token;
  }

  @Mutation
  updateRoles(roles: string[]) {
    this.roles = roles;
  }

  @Mutation
  startLogout() {
    this.loggedIn = false;
  }

  @Mutation
  reset() {
    this.nonReactive.data._token = null;
    this.loggedIn = false;
    this.loggedOut = true;
    this.roles = [];
  }

  @Action
  async logout() {
    this.startLogout();
  }
}
